import type {DateRange} from "@khan/component-date-range-dropdown";

import type {
    simpleCompletionQuery_coach_assignment_itemCompletionStates_bestScoreSkills as GqlBestScoreSkills,
    simpleCompletionQuery_coach_assignment_itemCompletionStates_bestScoreSkills_levelChanges as GqlLevelChanges,
    simpleCompletionQuery_coach_assignment_itemCompletionStates_bestScoreSkills_levelChanges_exercise as GqlLevelExercise,
} from "./__graphql-types__/simpleCompletionQuery.ts";

export type {getCoachedClassrooms_user_coachedClassrooms as CoachedClassroom} from "./__graphql-types__/getCoachedClassrooms.ts";

export interface StudentKaidAndNickname {
    kaid: string;
    coachNickname: string;
}

/**
 * TODO(miguel): Give this a more contextual name.
 */
export interface State {
    // The assignment that is being edited.  For new or existing assignments
    editingAssignment: EditingAssignmentState;
    // These are states we are tapping into Apollo to use for default values
    // later on
    students: Record<string, Array<Student>>;
    studentLists: Array<StudentList>;
    saving: boolean;
    isKhanmigoEnabled: boolean;
    hasGroupedExercises: boolean;
}

export type BestScoreSkills = GqlBestScoreSkills;
export type LevelChanges = GqlLevelChanges;
export type LevelExercise = GqlLevelExercise;

export interface Topic {
    id: string;
    key?: string;
    slug: string;
    title: string;
    iconPath?: string;
}

export interface StudentList {
    id: string;
    descriptor: string;
    signupCode: string;
    name?: string;
    students?: Array<Student>;
    // We could use the more exact type StudentKaidAndNickname here,
    // but we don't because some utility code assumes this is always a
    // student.  Luckily StudentKaidAndNickname is compatible with
    // Student (Student has some extra fields, but they're all
    // optional).
    studentKaidsAndNicknames?: Array<Student>;
    topics: Array<Topic>;
    assignmentsPage?: AssignmentsPage;
    countStudents?: number;
    isK4dClassroom?: boolean;
    isChildAssignmentsClassroom?: boolean;
}

export interface AssignmentsPage {
    assignments: Array<ExistingAssignment>;
    pageInfo: PageInfo;
}

export interface PageInfo {
    nextCursor: string | null | undefined;
}

export interface ExerciseProgress {
    log: Array<{
        correct: boolean;
    }>;
}

export interface Student {
    kaid: string;
    nickname?: string;
    coachNickname?: string;
    profileRoot?: string;
    exerciseProgress?: ExerciseProgress;
}

export type CompletionStateEnum =
    | "unstarted"
    | "in_progress"
    | "completed"
    | "late"
    | "missed";

export interface CompletionState {
    student?: Student;
    state: CompletionStateEnum;
    completedOn?: Date;
    bestScoreSkills?: BestScoreSkills;
    bestScore?: {
        numCorrect: number;
        numAttempted: number;
    };
    // for writing coach assignments only
    essaySession?: {id: string} | null | undefined;
}

export interface ExerciseAttempt {
    isCompleted: boolean;
    isRestarted: boolean;
    numAttempted: number;
    numCorrect: number;
    lastAttemptDate: Date;
}

export interface AIGuideThread {
    id: string;
    interactions?: Array<AIGuideInteraction>;
}

export interface AIGuideInteraction {
    id: string;
    answer: string;
    question: string;
}

export interface ActivitySubmission {
    threadID: string;
    thread?: AIGuideThread;
}

export type ItemCompletionState = CompletionState & {
    exerciseAttempts: Array<ExerciseAttempt>;
    activitySubmissions?: Array<ActivitySubmission>;
};

export interface TopicPathSegment {
    id: string;
    kind: string;
    slug: string;
}

export interface TopicPath {
    path?: Array<TopicPathSegment>;
}

export interface LearnableContent {
    id?: string;
    title: string;
    kind: string;
    name?: string;
    duration?: number;
    thumbnailUrl?: string;
    defaultUrlPath: string;
    topicPaths?: Array<TopicPath>;
}

export type AssignmentKind =
    | "content-assignments"
    | "subject-mastery-goal-assignments";

/**
 * The status given to the assignment.
 * @readonly
 * @enum {string}
 */
export const AssignmentStatus = Object.freeze({
    /**
     * The assignment is assigned (posted) to students.
     */
    ASSIGNED: "ASSIGNED" as const,

    /**
     * The assignment is scheduled to be assigned at some time in the future.
     */
    SCHEDULED: "SCHEDULED" as const,

    /**
     * The assignment has not been assigned to students yet.
     */
    DRAFT: "DRAFT" as const,
});

/**
 * The status given to an assignment, excluding the DRAFT status, as an enum
 * type. This is useful in cases where we know the API returns a value with
 * type `AssignmentStatus`, which may include DRAFT by definition, but we know
 * that in practice it will never include DRAFT (e.g. because the datastore
 * query filtered these out).
 */
export type NonDraftAssignmentStatusValues = Omit<
    typeof AssignmentStatus,
    "DRAFT"
>[keyof Omit<typeof AssignmentStatus, "DRAFT">];

/**
 * The status given to an assignment, as an enum type for use in Flow
 * type expressions.
 */
export type AssignmentStatusValues =
    (typeof AssignmentStatus)[keyof typeof AssignmentStatus];

/**
 * NOTE(miguel): this type needs to be cleaned up. It should not have an
 * optional id since that's only a valid case when we are creating assignments.
 * For all other cases we are dealing with assignments that have an id. This
 * gap causes code throughout the App to have falsy checks against the id
 * when that's not really possible making the code itself confusing. So we
 * should have a separate type for assignment creation that actually prevents
 * the id from existing since we can't pass an id to the server when creating an
 * assignment.
 *
 * Long term, we could have a base assignment type and enhance it with fields
 * specific to each query and use case.
 */
export interface Assignment {
    /**
     * Unique ID for this Assignment (see note above).
     */
    id: string | null | undefined;
    /**
     * The urlsafe string form of this assignment's datastore key.
     */
    key?: string;
    /**
     * The StudentList that this Assignment belongs to.
     */
    studentList: StudentList | null | undefined;
    /**
     * Whether this is a draft (an assignment that has not yet been posted).
     */
    isDraft: boolean;
    /**
     * Students of this assignment who are visible to the querying actor. Note
     * this list may be different from the list of studentKaids. For example,
     * certain support users may only be able to view a certain type of student,
     * not the full list of assignees.
     */
    students: Array<Student>;
    /**
     * Datetime when this Assignment was first assigned to a user.
     */
    assignedDate: Date | null | undefined;
    /**
     * Datetime when this assignment is due.
     */
    dueDate: Date;
    /**
     * The date used by teachers to schedule assignments to start (i.e. be
     * assigned) at some time in the future.
     */
    startDate: Date;
    /**
     * The status given to the assignment.
     */
    assignmentStatus?: AssignmentStatusValues;
    /**
     * The custom title for this assignment specified by the teacher.
     * As of 2/24, this is only used for Activity Assignments.
     */
    title: string | null | undefined;
    /**
     * Subject (Course) that this Assignment was assigned from.
     */
    subjectSlug?: string;
    totalCompletionStates?: Array<CompletionState>;
    itemCompletionStates?: Array<ItemCompletionState>;
    contentDescriptors: Array<string>;
    contents?: Array<LearnableContent>;
    questionSetType?: QuestionSetTypeEnum;
    exerciseConfig?:
        | {
              itemPickerStrategy: QuestionSetTypeEnum;
          }
        | null
        | undefined;
    numStudentsCompleted?: number;
    domainId: string;
    courseId: string;
    unitId: string;
    lessonId: string;
    /**
     * The instructions given to the students for the assignment.
     * Only used for AIGuideActivity assignments.
     */
    instructions?: string | null | undefined;
    configuredActivityInputs?: Record<string, string>;
}

export type ExistingAssignment = Assignment & {
    id: string;
};

export type StudentAssignment = Assignment & {
    id: string;
};

export interface SubjectMasteryAssignment {
    id: string;
    assignedDate: Date;
    dueDate: Date;
    // TODO(miguel) CLASS-3874: Consolidate course mastery goal
    // assignment types to use `course` in all places.
    subject: {
        title: string;
    };
}

export interface AssignmentInitialSettings {
    classDescriptors?: Array<string>;
    studentIds?: Array<string>;
    dueDate?: string;
    studentList?: {
        descriptor: string;
    };
}

export interface NewAssignment {
    assignment?: any;
    initialSettings?: AssignmentInitialSettings;
    preselectStudents?: boolean;
    storageKey?: string;
    persistSettings?: boolean;
}

export interface EditingAssignmentState {
    qualifiedContentDescriptorsList: Array<string>;
    classDescriptors: Array<string>;
    assignment: Assignment | null | undefined;
    initialSettings: AssignmentInitialSettings;
    preselectStudents: boolean;
}

export type QuestionSetTypeEnum = "RANDOM" | "SAME";

/**
 * Enum for possible filters to display assignments.
 * @readonly
 * @enum {string}
 */
export const Filters = Object.freeze({
    /**
     * Include assigned and past due assignments.
     *
     * NOTE: This is a mapping because we now use the term "assigned" to mean
     * "posted to students", but we keep the "active" value to map to the
     * current UI state.
     * TODO(CLASS-6691.7): Replace all the uses of "active" with "assigned".
     */
    ASSIGNED: "active",

    /**
     * Include scheduled assignents (to start in the future).
     */
    SCHEDULED: "scheduled",

    /**
     * Include saved drafts.
     */
    DRAFT: "draft",
});

/**
 * The different assignment collections used in the Teacher tools.
 */
export type FilterId = (typeof Filters)[keyof typeof Filters];

/**
 * The filters that can be applied to the assignments list.
 */
export interface AssignmentFilters {
    /**
     * Whether we want to include scheduled/draft assignments.
     * NOTE: The caller can use either this field or dueDateRange, but
     * cannot use them both together.
     */
    includeNotPostedToStudents: boolean | null | undefined;
    /**
     * Whether we want to include draft assignments.
     */
    isDraft: boolean | null | undefined;
    /**
     * A range of possible dates where the assignments due date is on.
     * NOTE: This cannot be used at the same time with
     * `includeNotPostedToStudents`. See comment above.
     */
    dueDateRange: DateRange | null | undefined;
    /**
     * A set of content kinds to filter assignments by.
     */
    contentKinds: string[] | null | undefined;
    /**
     * A set of course ids to filter assignments by.
     */
    courseIDs: string[] | null | undefined;
}

// "COPY": making a copy of an assigned, scheduled, or draft assignment
// "EDIT": edit to an assigned or scheduled assignment
// "ASSIGN": making a new assignment or editing a draft assignment
export type NewAssignmentModalTitle = "COPY" | "EDIT" | "ASSIGN";

/**
 * A type the represents the parameters when creating new assignments.
 */
export interface NewAssignmentSubmitParameters {
    /**
     * The assignment in memory
     */
    assignment: Assignment;
    /**
     * A list of content descriptors for the assigned content
     */
    qualifiedContentDescriptors: string[];
    /**
     * The total number of students in the classroom
     */
    numStudentsInClass: number;
    /**
     * A list of created assignments, which could be undefined if the mutation
     * failed
     */
    createdAssignments?: Array<Assignment>;
}

/**
 * A type that represents a full DCUL path for a content item, used to attach
 * the correct DCUL information to an assignment.
 */
export interface DCULPath {
    domainId?: string | null | undefined;
    courseId?: string | null | undefined;
    unitId?: string | null | undefined;
    lessonId?: string | null | undefined;
    contentKind:
        | "AIGuideActivity"
        | "Article"
        | "Challenge"
        | "CourseChallenge"
        | "Exercise"
        | "Interactive"
        | "Project"
        | "Scratchpad"
        | "Talkthrough"
        | "TopicQuiz"
        | "TopicUnitTest"
        | "Video"
        | "AIGuideActivity";
    contentId: string;
}

export type UserHasChildAssignmentClassrooms = "ALL" | "SOME" | "NONE";
